import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Center,
  Group,
  Image,
  Loader,
  Modal,
  rem,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { keyframes } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useDevicePolling from "../../../hooks/useDevicePolling";
import { getDeviceImg } from "../../../utils/productUtils";
import DeviceSelector from "../../test-suite/DeviceSelector";
import TitleWithInfoLink from "../../new_test_ui/new_test_steps/common/TitleWithInfoLink";
import { USER_GUIDE_DOC_URL } from "@constants/externalTutorialLinks";
import { selectTestStatus } from "@store/tests/testSelectors";
import { TEST_IN_PROGRESS_STATUSES } from "types/testTypes";

const MODAL_SECTION_BORDER = "1px solid var(--oculera-border)";
// The title lives in the sticky header so it stays put while the cards scroll.
const DEVICE_SELECTOR_MODAL_STYLES = {
  header: { borderBottom: MODAL_SECTION_BORDER },
  body: { paddingTop: "var(--mb-padding, var(--mantine-spacing-md))" },
};

const ATTENTION_PATHS = ["/new-test", "/new-test-ui", "/device", "/version-control"];

// How often to re-poll /ws/now while the device picker is open.
const DEVICE_STATUS_POLL_MS = 5000;

const IMAGE_SIZE = rem(32);

// The nav (and this component) remounts on every route change, so a ref would
// re-trigger the auto-open on each navigation. Once per full page load.
let hasAutoOpenedThisPageLoad = false;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(21, 125, 191, 0); }
  50% { box-shadow: 0 0 0 6px rgba(21, 125, 191, 0.45); }
`;

const useStyles = createStyles((theme, _, u) => ({
  trigger: {
    backgroundColor: theme.colors.gray[0],
    borderRadius: theme.radius.md,
    padding: `${rem(4)} ${theme.spacing.sm}`,
    border: "1px solid var(--oculera-border)",
    transition: "background-color 150ms ease",
    width: rem(280),
    [`@media (max-width: ${theme.breakpoints.xs})`]: {
      width: "auto",
    },
    "&:hover": {
      backgroundColor: theme.colors.gray[2],
    },
    [u.dark]: {
      backgroundColor: theme.colors.dark[5],
      "&:hover": {
        backgroundColor: theme.colors.dark[4],
      },
    },
  },
  triggerAttention: {
    animation: `${glow} 1.6s ease-in-out infinite`,
    borderColor: theme.colors.blue[5],
  },
  triggerDisabled: {
    cursor: "not-allowed",
    opacity: 0.55,
    "&:hover": {
      backgroundColor: theme.colors.gray[0],
    },
    [u.dark]: {
      "&:hover": {
        backgroundColor: theme.colors.dark[5],
      },
    },
  },
  textBlock: {
    minWidth: 0,
    flex: 1,
    [`@media (max-width: ${theme.breakpoints.xs})`]: {
      display: "none",
    },
  },
}));

export default function GlobalDeviceSelector() {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const [opened, setOpened] = useState(false);
  const { devices, selectedDevice, loading, handleDeviceChange, refresh, refreshStatuses } =
    useDevicePolling({
      enabled: true,
    });
  const location = useLocation();
  const testStatus = useSelector(selectTestStatus);
  const isTestInProgress = TEST_IN_PROGRESS_STATUSES.has(testStatus);

  useEffect(() => {
    setOpened(false);
  }, [location.pathname]);

  // Force the modal closed if a test starts while the modal happens to be open.
  useEffect(() => {
    if (isTestInProgress) setOpened(false);
  }, [isTestInProgress]);

  const onDeviceRelevantPath = useMemo(
    () =>
      ATTENTION_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`)),
    [location.pathname],
  );

  // No remembered device selection survived startup (never chosen, or the
  // choice expired): open the picker so the user makes an explicit choice
  // instead of silently working against a device they never selected. Only on
  // pages where the device matters — elsewhere the trigger just shows
  // "Select Device".
  useEffect(() => {
    if (hasAutoOpenedThisPageLoad) return;
    if (!onDeviceRelevantPath) return;
    if (loading || isTestInProgress || selectedDevice || devices.length === 0) return;
    hasAutoOpenedThisPageLoad = true;
    setOpened(true);
  }, [loading, isTestInProgress, selectedDevice, devices.length, onDeviceRelevantPath]);

  // While the picker is open, keep every device's connection status live — not
  // just the selected one (which DeviceStatusBadge's scene polling already
  // covers). This re-polls /ws/now only (cheap), so the CONNECTED/NOT
  // CONNECTED badges reflect reality without re-fetching the device list. The
  // initial refresh() on open seeds the first frame; this fills in the rest.
  useEffect(() => {
    if (!opened) return undefined;
    const id = setInterval(refreshStatuses, DEVICE_STATUS_POLL_MS);
    return () => clearInterval(id);
  }, [opened, refreshStatuses]);

  const theme = useMantineTheme();
  const skeletonTextH = theme.spacing.sm;

  const displayedDevice = selectedDevice;

  const isFetching = !displayedDevice && loading;
  const isIdle = !displayedDevice && !loading;
  const noDevicesRegistered = isIdle && devices.length === 0;
  const idleLabel = noDevicesRegistered ? t("noDeviceRegistered") : t("SelectDevice");
  const shouldGlow = !isTestInProgress && !displayedDevice && onDeviceRelevantPath;

  const handleTriggerClick = () => {
    if (isTestInProgress) return;
    setOpened(true);
    refresh();
  };

  const trigger = (
    <UnstyledButton
      className={cx(
        "global-device-selector",
        classes.trigger,
        shouldGlow && classes.triggerAttention,
        isTestInProgress && classes.triggerDisabled,
      )}
      onClick={handleTriggerClick}
      aria-label={idleLabel}
      aria-disabled={isTestInProgress}
      data-testid="global-device-selector-trigger"
    >
      <Group gap="sm" wrap="nowrap" align="center" mih={IMAGE_SIZE}>
        {isIdle ? (
          <Text fz="sm" fw={600} truncate style={{ flex: 1 }}>
            {idleLabel}
          </Text>
        ) : isFetching ? (
          <>
            <Skeleton h={IMAGE_SIZE} w={IMAGE_SIZE} radius="sm" style={{ flexShrink: 0 }} />
            <Box className={classes.textBlock}>
              <Stack gap="xs">
                <Skeleton h={skeletonTextH} w="70%" />
                <Skeleton h={theme.spacing.xs} w="50%" />
              </Stack>
            </Box>
          </>
        ) : (
          displayedDevice && (
            <>
              <Image
                src={getDeviceImg(displayedDevice.uuid)}
                h={IMAGE_SIZE}
                w={IMAGE_SIZE}
                fit="contain"
                alt={t("DeviceCard.vrDevice")}
                style={{ flexShrink: 0 }}
              />
              <Box className={classes.textBlock}>
                <Stack gap={0}>
                  <Text fz="sm" fw={600} truncate>
                    {displayedDevice.alias || displayedDevice.uuid}
                  </Text>
                  {displayedDevice.alias && (
                    <Text
                      fz="xs"
                      c="dimmed"
                      ff="monospace"
                      style={{ letterSpacing: "0.5px" }}
                      truncate
                    >
                      {displayedDevice.uuid}
                    </Text>
                  )}
                </Stack>
              </Box>
            </>
          )
        )}
      </Group>
    </UnstyledButton>
  );

  const tooltipLabel = isTestInProgress ? (
    <Text fz="xs">
      {t("GlobalDeviceSelector.lockedDuringTest", "End the test before switching devices.")}
    </Text>
  ) : displayedDevice ? (
    <Stack gap={0}>
      {displayedDevice.alias && <Text fz="xs">{displayedDevice.alias}</Text>}
      {displayedDevice.uuid && <Text fz="xs">{displayedDevice.uuid}</Text>}
    </Stack>
  ) : null;

  return (
    <>
      {tooltipLabel ? (
        <Tooltip withArrow position="bottom" label={tooltipLabel}>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        size="xl"
        centered
        zIndex={1000}
        title={
          <TitleWithInfoLink
            size="h4"
            title={t("SelectDevice")}
            tooltipText={t("InfoButtonTooltip")}
            link={USER_GUIDE_DOC_URL}
          />
        }
        styles={DEVICE_SELECTOR_MODAL_STYLES}
      >
        {loading && devices.length === 0 ? (
          <Center py="md">
            <Loader size="sm" />
          </Center>
        ) : devices.length > 0 ? (
          <DeviceSelector
            hideTitle
            devices={devices}
            selectedDeviceId={selectedDevice?.uuid}
            onDeviceSelect={(device) => {
              handleDeviceChange(device);
              setOpened(false);
            }}
          />
        ) : (
          <Text>{t("DeviceCard.noDevicesAvailable")}</Text>
        )}
      </Modal>
    </>
  );
}
